import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SendIcon, UsersIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Field, Toggle, inputClass } from '../../components/ui/Field';
import { eventTitle, formatDate, functionLabel, functionOrder, fullName, priorityClass, priorityLabel, voiceLabel, voiceOrder } from '../../utils/labels';
import type { ChoirFunction, Priority, Voice } from '../../types';
type Obseg = 'aktivni' | 'vsi' | 'glasovi' | 'funkcije' | 'dogodek' | 'rocno';
const obsegi: {
  key: Obseg;
  label: string;
}[] = [{
  key: 'aktivni',
  label: 'Vsi aktivni člani'
}, {
  key: 'vsi',
  label: 'Vsi člani in gostje'
}, {
  key: 'glasovi',
  label: 'Izbrane glasovne skupine'
}, {
  key: 'funkcije',
  label: 'Izbrane funkcije'
}, {
  key: 'dogodek',
  label: 'Udeleženci dogodka'
}, {
  key: 'rocno',
  label: 'Ročno izbrani člani'
}];
export function AdminAnnouncementForm() {
  const navigate = useNavigate();
  const {
    members,
    upcoming,
    expectedFor,
    addAnnouncement,
    pushToast,
    currentMember
  } = useApp();
  const [naslov, setNaslov] = useState('');
  const [besedilo, setBesedilo] = useState('');
  const [prioriteta, setPrioriteta] = useState<Priority>('obicajno');
  const [obseg, setObseg] = useState<Obseg>('aktivni');
  const [glasovi, setGlasovi] = useState<Voice[]>([]);
  const [funkcije, setFunkcije] = useState<ChoirFunction[]>([]);
  const [dogodekId, setDogodekId] = useState(upcoming[0]?.id ?? '');
  const [rocno, setRocno] = useState<string[]>([]);
  const [zahtevaPotrditev, setZahtevaPotrditev] = useState(false);
  const prejemniki = useMemo(() => {
    switch (obseg) {
      case 'aktivni':
        return members.filter(m => m.type === 'aktivni');
      case 'vsi':
        return members.filter(m => m.type !== 'nekdanji');
      case 'glasovi':
        return members.filter(m => glasovi.includes(m.voice) && m.type !== 'nekdanji');
      case 'funkcije':
        return members.filter(m => m.functions.some(f => funkcije.includes(f)));
      case 'dogodek':
        {
          const e = upcoming.find(x => x.id === dogodekId);
          return e ? expectedFor(e) : [];
        }
      case 'rocno':
        return members.filter(m => rocno.includes(m.id));
      default:
        return [];
    }
  }, [obseg, members, glasovi, funkcije, dogodekId, rocno, upcoming, expectedFor]);
  const obcinstvoLabel = useMemo(() => {
    switch (obseg) {
      case 'aktivni':
        return 'Vsi aktivni člani';
      case 'vsi':
        return 'Vsi člani in gostje';
      case 'glasovi':
        return glasovi.map(v => voiceLabel[v]).join(', ') || 'Izbrane glasovne skupine';
      case 'funkcije':
        return funkcije.map(f => functionLabel[f]).join(', ') || 'Izbrane funkcije';
      case 'dogodek':
        {
          const e = upcoming.find(x => x.id === dogodekId);
          return e ? `Udeleženci: ${eventTitle(e)}` : 'Udeleženci dogodka';
        }
      case 'rocno':
        return `Ročni izbor (${rocno.length})`;
      default:
        return '';
    }
  }, [obseg, glasovi, funkcije, dogodekId, rocno, upcoming]);
  function poslji() {
    addAnnouncement({
      id: `a${Math.random().toString(36).slice(2, 7)}`,
      naslov: naslov || 'Obvestilo zbora',
      besedilo,
      prioriteta,
      avtor: currentMember ? fullName(currentMember) : 'Vodstvo zbora',
      objavljeno: new Date().toISOString(),
      obcinstvoLabel,
      prejemniki: prejemniki.map(m => m.id),
      zahtevaPotrditev,
      prebrali: []
    });
    pushToast(`Obvestilo je bilo poslano ${prejemniki.length} članom.`);
    navigate('/admin/obvestila');
  }
  return <div className="mx-auto max-w-5xl space-y-4" data-id="element-469" data-mp="3dmih76w6ppxa">
      <button onClick={() => navigate('/admin/obvestila')} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-470" data-mp="3dk5ru8o3kda7">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-471" data-mp="162f985zayir4" />
        Nazaj na obvestila
      </button>

      <div data-id="element-472" data-mp="164i699yvhc5c">
        <h1 className="text-2xl font-semibold text-ink" data-id="element-473" data-mp="re2m7yi70a8r">Novo obvestilo</h1>
        <p className="mt-0.5 text-sm text-ink-muted" data-id="element-474" data-mp="rbpyf4pr75ns">
          Uradno sporočilo bo članom vidno v portalu, po potrebi z zahtevo za potrditev branja.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]" data-id="element-475" data-mp="1k2ztz7os2on9">
        <Card className="space-y-4 p-5" data-id="element-476" data-mp="1p22xgnp40mt4">
          <Field label="Naslov" htmlFor="naslov" data-id="element-477" data-mp="1p11e7vxxwu0b">
            <input id="naslov" value={naslov} onChange={e => setNaslov(e.target.value)} placeholder="Npr. Vaja prestavljena na soboto" className={inputClass} data-id="element-478" data-mp="1b2be07ftqe7q" />
          </Field>

          <Field label="Sporočilo" htmlFor="besedilo" data-id="element-479" data-mp="3vu3rfsvqx1gr">
            <textarea id="besedilo" rows={9} value={besedilo} onChange={e => setBesedilo(e.target.value)} placeholder="Napišite jasno in kratko sporočilo članom…" className={inputClass} data-id="element-480" data-mp="3p9n5t69xm9x" />
          </Field>

          <div data-id="element-481" data-mp="61u02b1agvfu">
            <p className="mb-2 text-[13px] font-medium text-ink" data-id="element-482" data-mp="62d5wtdnztdi">Prioriteta</p>
            <div className="flex flex-wrap gap-2" data-id="element-483" data-mp="14ts1ukvpim3">
              {(['obicajno', 'pomembno', 'nujno', 'sprememba'] as Priority[]).map(p => <button key={p} type="button" onClick={() => setPrioriteta(p)} aria-pressed={prioriteta === p} className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${prioriteta === p ? 'border-green-500 bg-green-500 text-white' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`} data-id="element-484" data-mp="39hhfyk7dha2o">
                  {priorityLabel[p]}
                </button>)}
            </div>
          </div>

          <Toggle checked={zahtevaPotrditev} onChange={setZahtevaPotrditev} label="Zahtevaj potrditev branja" opis="Člani bodo morali klikniti „Prebral/a sem“." data-id="element-485" data-mp="3fiyrmfjov76d" />
        </Card>

        <div className="space-y-4" data-id="element-486" data-mp="3p8s1nc46ne8i">
          <Card className="space-y-4 p-5" data-id="element-487" data-mp="2sjaq4giiqxyw">
            <Field label="Prejemniki" htmlFor="obseg" data-id="element-488" data-mp="2skm1p2i7ha0k">
              <select id="obseg" value={obseg} onChange={e => setObseg(e.target.value as Obseg)} className={inputClass} data-id="element-489" data-mp="2wkmg4n5b1l7m">
                {obsegi.map(o => <option key={o.key} value={o.key} data-id="element-490" data-mp="36jv7r9cp6nqt">
                    {o.label}
                  </option>)}
              </select>
            </Field>

            {obseg === 'glasovi' && <div className="flex flex-wrap gap-2" data-id="element-491" data-mp="lr9w5bbvujb9">
                {voiceOrder.filter(v => v !== 'brez').map(v => <button key={v} type="button" onClick={() => setGlasovi(g => g.includes(v) ? g.filter(x => x !== v) : [...g, v])} aria-pressed={glasovi.includes(v)} className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${glasovi.includes(v) ? 'border-purple-300 bg-purple-50 text-purple-700' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`} data-id="element-492" data-mp="21biuoekb01zd">
                      {voiceLabel[v]}
                    </button>)}
              </div>}

            {obseg === 'funkcije' && <div className="flex flex-wrap gap-2" data-id="element-493" data-mp="32z1qtzj1kpmk">
                {functionOrder.map(f => <button key={f} type="button" onClick={() => setFunkcije(x => x.includes(f) ? x.filter(y => y !== f) : [...x, f])} aria-pressed={funkcije.includes(f)} className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${funkcije.includes(f) ? 'border-green-300 bg-green-50 text-green-700' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`} data-id="element-494" data-mp="2bxvd1y9hczfb">
                    {functionLabel[f]}
                  </button>)}
              </div>}

            {obseg === 'dogodek' && <Field label="Dogodek" htmlFor="dogodek" data-id="element-495" data-mp="3ik8o4a32erhc">
                <select id="dogodek" value={dogodekId} onChange={e => setDogodekId(e.target.value)} className={inputClass} data-id="element-496" data-mp="3ijp8x14suh70">
                  {upcoming.map(e => <option key={e.id} value={e.id} data-id="element-497" data-mp="1gn8c41and96c">
                      {formatDate(e.datum)} — {eventTitle(e)}
                    </option>)}
                </select>
              </Field>}

            {obseg === 'rocno' && <div className="max-h-56 overflow-y-auto viva-scroll rounded-lg border border-line p-2" data-id="element-498" data-mp="1qmz5fznh1i4u">
                {members.filter(m => m.type !== 'nekdanji').map(m => <label key={m.id} className="flex items-center gap-2 px-2 py-1.5 text-[13px] text-ink" data-id="element-499" data-mp="1umgairyzkd9h">
                      <input type="checkbox" className="h-4 w-4 rounded border-line accent-green-500" checked={rocno.includes(m.id)} onChange={() => setRocno(r => r.includes(m.id) ? r.filter(x => x !== m.id) : [...r, m.id])} data-id="element-500" data-mp="579zjng9g50d" />
                      {fullName(m)}
                    </label>)}
              </div>}

            <div className="flex items-center gap-2 rounded-lg bg-canvas px-3 py-2.5" data-id="element-501" data-mp="2wl3lpctafjlp">
              <UsersIcon className="h-4 w-4 text-green-600" data-id="element-502" data-mp="3tb42opnf0vsg" />
              <p className="text-[13px] text-ink" data-id="element-503" data-mp="3tcp2ousi16xp">
                <span className="font-semibold" data-id="element-504" data-mp="3pa23g8ti0jqh">{prejemniki.length}</span> prejemnikov · {obcinstvoLabel}
              </p>
            </div>
          </Card>

          <Card className="p-5" data-id="element-505" data-mp="3kfxy1tzvsjri">
            <h2 className="text-base font-semibold text-ink" data-id="element-506" data-mp="3fjzd6gdrhjvu">Predogled</h2>
            <div className="mt-3 rounded-lg border border-line p-4" data-id="element-507" data-mp="gt7d1stw0rta">
              <Badge className={priorityClass[prioriteta]} data-id="element-508" data-mp="gvaagxxddn81">{priorityLabel[prioriteta]}</Badge>
              <p className="mt-2 text-sm font-semibold text-ink" data-id="element-509" data-mp="c163ieo3tf6y">{naslov || 'Naslov obvestila'}</p>
              <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-ink-muted" data-id="element-510" data-mp="jfgboxulmzkw">
                {besedilo || 'Vsebina obvestila se bo prikazala tukaj.'}
              </p>
              {zahtevaPotrditev && <p className="mt-3 rounded-md bg-canvas px-2.5 py-1.5 text-[12px] text-ink-muted" data-id="element-511" data-mp="xdes08shd631">
                  Član bo videl gumb „Prebral/a sem“.
                </p>}
            </div>
          </Card>

          <div className="flex gap-2" data-id="element-512" data-mp="17b15cqu4spkw">
            <Button variant="secondary" full onClick={() => navigate('/admin/obvestila')} data-id="element-513" data-mp="179zt4j1dpx1l">
              Prekliči
            </Button>
            <Button full onClick={poslji} disabled={!besedilo || prejemniki.length === 0} data-id="element-514" data-mp="1h1e0te3hi35t">
              <SendIcon className="h-4 w-4" data-id="element-515" data-mp="1h36y42s7jb15" />
              Pošlji obvestilo
            </Button>
          </div>
        </div>
      </div>
    </div>;
}